import React from 'react';
import { Info, Shield, Clock, Download, Filter, Globe, Mail } from 'lucide-react';
import { Translations } from '../i18n/translations';
import { ControlBarLegend } from './ControlBarLegend';

interface AboutPanelProps {
  t: Translations;
}

export const AboutPanel: React.FC<AboutPanelProps> = ({ t }) => {
  return (
    <section className="bg-gray-50 dark:bg-gray-900 border-b-4 border-gray-300 dark:border-gray-600" aria-labelledby="about-heading">
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <h2 id="about-heading" className="text-xl sm:text-2xl font-bold font-serif mb-4 sm:mb-6 text-gray-900 dark:text-white">{t.aboutTitle}</h2>

        <div className="grid gap-4 md:grid-cols-2">
          {/* What is this */}
          <article className="bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 p-4">
            <h3 className="font-bold mb-2 flex items-center gap-2 text-gray-900 dark:text-white">
              <Info className="w-4 h-4" aria-hidden="true" />
              {t.aboutWhatIsThis}
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              {t.aboutWhatIsThisText}
            </p>
          </article>

          {/* How to Use */}
          <article className="bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 p-4">
            <h3 className="font-bold mb-3 text-gray-900 dark:text-white">{t.aboutHowToUse}</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <li>{t.aboutHowToUseStep1}</li>
              <li>{t.aboutHowToUseStep2}</li>
              <li>{t.aboutHowToUseStep3}</li>
              <li>{t.aboutHowToUseStep4}</li>
            </ol>
          </article>

          {/* Digital Minimalism */}
          <article className="bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 p-4">
            <h3 className="font-bold mb-2 flex items-center gap-2 text-gray-900 dark:text-white">
              <Clock className="w-4 h-4" aria-hidden="true" />
              {t.aboutDigitalMinimalism}
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              {t.aboutDigitalMinimalismText}
            </p>
          </article>

          {/* Privacy */}
          <article className="bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 p-4">
            <h3 className="font-bold mb-2 flex items-center gap-2 text-gray-900 dark:text-white">
              <Shield className="w-4 h-4" aria-hidden="true" />
              {t.aboutPrivacy}
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              {t.aboutPrivacyText}
            </p>
          </article>

          {/* Backup & Restore */}
          <article className="bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 p-4">
            <h3 className="font-bold mb-2 flex items-center gap-2 text-gray-900 dark:text-white">
              <Download className="w-4 h-4" aria-hidden="true" />
              {t.aboutBackupRestore}
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              {t.aboutBackupRestoreText}
            </p>
          </article>

          {/* Clickbait Filter */}
          <article className="bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 p-4">
            <h3 className="font-bold mb-2 flex items-center gap-2 text-gray-900 dark:text-white">
              <Filter className="w-4 h-4" aria-hidden="true" />
              {t.aboutClickbaitFilter}
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              {t.aboutClickbaitFilterText}
            </p>
          </article>

          {/* Made in Europe */}
          <article className="bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 p-4">
            <h3 className="font-bold mb-2 flex items-center gap-2 text-gray-900 dark:text-white">
              <Globe className="w-4 h-4" aria-hidden="true" />
              {t.aboutMadeInEurope}
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              {t.aboutMadeInEuropeText}
            </p>
          </article>

          {/* Personal Project */}
          <article className="bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 p-4">
            <h3 className="font-bold mb-2 flex items-center gap-2 text-gray-900 dark:text-white">
              <Mail className="w-4 h-4" aria-hidden="true" />
              {t.aboutPersonalProject}
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              {t.aboutPersonalProjectText}{' '}
              <a
                href="mailto:therealfrontpage@kmazur.me"
                className="text-blue-700 dark:text-blue-400 underline hover:text-blue-900 dark:hover:text-blue-300"
              >
                {t.aboutFeedbackLink}
              </a>
            </p>
          </article>
        </div>

        {/* Control Bar Legend */}
        <div className="mt-4">
          <ControlBarLegend t={t} />
        </div>

        {/* Buy Me a Coffee */}
        <div className="mt-6 text-center">
          <a
            href="https://buymeacoffee.com/chrismazur"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium border-2 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            ☕ {t.buyMeCoffee}
          </a>
        </div>
      </div>
    </section>
  );
};
